import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const Section = ({ title, children }) => (
  <div className="mt-10">
    {title && <h2 className="font-heading font-black tracking-tight text-navy text-2xl mb-4">{title}</h2>}
    {children}
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-base leading-relaxed">
    {items.map((i) => <li key={i}>{i}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <Seo title="Privacy Policy | Prime Pro Connect Services"
        description="Read how Prime Pro Connect Services collects, uses and protects personal information submitted through service requests, our website and messaging platforms."
        path="/privacy-policy" />
      <header className="bg-navy border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <Link to="/" data-testid="privacy-logo-link" className="flex items-center gap-3">
            <span className="w-8 h-8 bg-flame flex items-center justify-center font-heading font-black text-white text-lg">P</span>
            <span className="font-heading font-black text-white tracking-tight">PRIME PRO</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-3">Legal</p>
        <h1 data-testid="privacy-heading" className="font-heading font-black tracking-tighter text-navy text-4xl sm:text-5xl">
          Privacy Policy
        </h1>

        <p className="text-slate-600 text-base leading-relaxed mt-8">
          Prime Pro Connect Services respects your privacy and is committed to protecting
          the personal information you provide to us.
        </p>
        <p className="text-slate-600 text-base leading-relaxed mt-4">
          When you contact us, submit a service request, use our website, Messenger
          automation, advertising forms, or other communication channels, we may collect
          information such as:
        </p>
        <div className="mt-4">
          <List items={[
            "Name",
            "Phone number",
            "Email address",
            "Service address",
            "City and ZIP code",
            "Requested service",
            "Preferred method of contact",
            "Preferred appointment date or time",
            "Information voluntarily provided about the service request",
          ]} />
        </div>

        <Section title="How We Use Your Information">
          <p className="text-slate-600 text-base leading-relaxed mb-4">We use this information to:</p>
          <List items={[
            "Respond to service requests",
            "Connect customers with appropriate local service professionals",
            "Coordinate estimates, appointments, and service requests",
            "Communicate with customers regarding their request",
            "Improve our customer service and service-matching process",
          ]} />
        </Section>

        <Section title="Information Sharing">
          <p className="text-slate-600 text-base leading-relaxed">
            Prime Pro Connect Services does not sell personal information.
          </p>
          <p className="text-slate-600 text-base leading-relaxed mt-4">
            Relevant information may be shared with local service professionals when
            necessary to respond to a customer's service request or provide the requested
            estimate or service.
          </p>
        </Section>

        <Section title="Data Protection & Your Rights">
          <p className="text-slate-600 text-base leading-relaxed">
            We take reasonable measures to protect personal information.
          </p>
          <p className="text-slate-600 text-base leading-relaxed mt-4">
            Customers may request access to or deletion of their personal information by
            contacting Prime Pro Connect Services through our official business
            communication channels.
          </p>
        </Section>

        <Section title="Advertising and Messaging">
          <p className="text-slate-600 text-base leading-relaxed">
            Prime Pro Connect Services may use platforms such as Facebook, Instagram,
            Messenger, WhatsApp, ManyChat, and other advertising or communication platforms
            to receive and respond to service requests.
          </p>
          <p className="text-slate-600 text-base leading-relaxed mt-4">
            Information submitted through these platforms may be processed according to
            their respective privacy policies in addition to this Privacy Policy.
          </p>
        </Section>

        <p className="text-slate-400 text-sm mt-12 border-t border-border pt-6">
          Last Updated: August 27, 2026
        </p>
      </main>

      <footer className="bg-navy py-8">
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs">© 2026 Prime Pro Connect Services. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/" data-testid="privacy-footer-home" className="text-white/50 hover:text-flame text-xs transition-colors">Home</Link>
            <Link to="/privacy-policy" data-testid="privacy-footer-privacy" className="text-white/50 hover:text-flame text-xs transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
